import { Invoice } from '@principle-theorem/principle-core';
import {
  CollectionGroup,
  type IAccountCredit,
  type IBrand,
  type IOrganisation,
  type IPatient,
  type PaidInvoice,
} from '@principle-theorem/principle-core/interfaces';
import {
  Firestore,
  asDocRef,
  collectionGroupQuery,
  orderBy,
  snapshot,
  toTimestamp,
  where,
  type Query,
  type WithRef,
} from '@principle-theorem/shared';
import { BigQueryJobName } from '../../job-names';
import { IAccountCreditWriteTopicData } from '../../topic-data/topic-data';
import {
  BasicBigQuerySyncHandler,
  IBigQuerySyncRequest,
} from '../big-query-sync';

export class BigQueryAccountCreditSyncHandler extends BasicBigQuerySyncHandler<
  IAccountCredit,
  IAccountCreditWriteTopicData
> {
  readonly bigQueryJobName = BigQueryJobName.AccountCreditWrite;

  getQuery(request: IBigQuerySyncRequest): Query<IAccountCredit> {
    return collectionGroupQuery(
      CollectionGroup.AccountCredits,
      where('practiceRef', '==', request.practiceRef),
      where('deleted', '==', false),
      where('createdAt', '>=', request.range.from),
      where('createdAt', '<=', request.range.to),
      orderBy('createdAt')
    );
  }

  async getTopicData(
    accountCredit: WithRef<IAccountCredit>
  ): Promise<IAccountCreditWriteTopicData> {
    const invoice = accountCredit.invoice
      ? await Firestore.getDoc(asDocRef<PaidInvoice>(accountCredit.invoice))
      : undefined;
    const patientRef = Firestore.getParentDocRef<IPatient>(accountCredit.ref);
    const brandRef = Firestore.getParentDocRef<IBrand>(patientRef);
    const organisationRef = Firestore.getParentDocRef<IOrganisation>(brandRef);
    const transactions = invoice
      ? await snapshot(Invoice.transactions$(invoice))
      : [];
    return {
      organisation: await Firestore.getDoc(organisationRef),
      brand: await Firestore.getDoc(brandRef),
      practice: accountCredit.practiceRef
        ? await Firestore.getDoc(accountCredit.practiceRef)
        : undefined,
      patient: await Firestore.getDoc(patientRef),
      accountCredit,
      invoice,
      transactions,
      timestamp: toTimestamp(),
    };
  }
}
